import React from "react";

import {
    Box,
    Chip,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import StarIcon from "@material-ui/icons/Star";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
    skeleton: {
        borderRadius: 8,
        backgroundColor: theme.mode === "dark" ? "#4a4a4a" : "#ffffff"
    }
}));

const getSurface = (isDark) => ({
    panel: isDark ? "#4a4a4a" : "#ffffff",
    row: isDark ? "#505050" : "#f8fbff",
    border: isDark ? "rgba(255,255,255,0.10)" : "#dbe4f0",
    text: isDark ? "#f8fafc" : "#111827",
    muted: isDark ? "#cbd5e1" : "#64748b",
    head: isDark ? "#3f3f3f" : "#eef4ff"
});

function formatTime(minutes) {
    const parsed = Number(minutes);
    const total = Number.isFinite(parsed) ? Math.max(Math.round(parsed), 0) : 0;
    const hours = Math.floor(total / 60);
    const mins = total % 60;

    return `${String(hours).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m`;
}

function formatRating(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2).replace(".", ",") : "0,00";
}

function EmptyRows({ isDark }) {
    const surface = getSurface(isDark);

    return (
        <TableRow>
            <TableCell colSpan={7}>
                <Box sx={{ py: 4, textAlign: "center" }}>
                    <Typography sx={{ color: surface.text, fontWeight: 800 }}>
                        Nenhum atendente encontrado
                    </Typography>
                    <Typography sx={{ color: surface.muted, fontSize: 13, mt: 0.5 }}>
                        Ajuste o periodo ou aguarde novos atendimentos.
                    </Typography>
                </Box>
            </TableCell>
        </TableRow>
    );
}

export default function TableAttendantsStatus(props) {
    const { loading, attendants = [] } = props;
    const theme = useTheme();
    const classes = useStyles();
    const isDark = theme.mode === "dark";
    const surface = getSurface(isDark);

    if (loading) {
        return <Skeleton className={classes.skeleton} variant="rectangular" height={220} />;
    }

    return (
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{
                borderRadius: "8px",
                border: `1px solid ${surface.border}`,
                backgroundColor: surface.panel,
                boxShadow: "none",
                overflow: "auto"
            }}
        >
            <Table size="small">
                <TableHead>
                    <TableRow sx={{ backgroundColor: surface.head }}>
                        <TableCell sx={{ color: surface.muted, fontWeight: 900, fontSize: 12, textTransform: "uppercase" }}>
                            {i18n.t("dashboard.users.name")}
                        </TableCell>
                        <TableCell align="center" sx={{ color: surface.muted, fontWeight: 900, fontSize: 12, textTransform: "uppercase" }}>
                            {i18n.t("dashboard.assessments.score")}
                        </TableCell>
                        <TableCell align="center" sx={{ color: surface.muted, fontWeight: 900, fontSize: 12, textTransform: "uppercase" }}>
                            {i18n.t("dashboard.assessments.ratedCalls")}
                        </TableCell>
                        <TableCell align="center" sx={{ color: surface.muted, fontWeight: 900, fontSize: 12, textTransform: "uppercase" }}>
                            {i18n.t("dashboard.assessments.totalCalls")}
                        </TableCell>
                        <TableCell align="center" sx={{ color: surface.muted, fontWeight: 900, fontSize: 12, textTransform: "uppercase" }}>
                            {i18n.t("dashboard.cards.averageWaitingTime")}
                        </TableCell>
                        <TableCell align="center" sx={{ color: surface.muted, fontWeight: 900, fontSize: 12, textTransform: "uppercase" }}>
                            {i18n.t("dashboard.cards.averageServiceTime")}
                        </TableCell>
                        <TableCell align="center" sx={{ color: surface.muted, fontWeight: 900, fontSize: 12, textTransform: "uppercase" }}>
                            {i18n.t("dashboard.cards.status")}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {attendants.length === 0 ? (
                        <EmptyRows isDark={isDark} />
                    ) : (
                        attendants.map((attendant) => (
                            <TableRow
                                key={attendant.id}
                                sx={{
                                    "&:nth-of-type(even)": {
                                        backgroundColor: surface.row
                                    },
                                    "& td": {
                                        borderBottom: `1px solid ${surface.border}`,
                                        color: surface.text,
                                        py: 1.25
                                    }
                                }}
                            >
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={1.25}>
                                        <Box
                                            sx={{
                                                width: 34,
                                                height: 34,
                                                borderRadius: "50%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#ffffff",
                                                fontWeight: 900,
                                                backgroundColor: attendant.online ? "#005AE5" : "#64748b"
                                            }}
                                        >
                                            {String(attendant.name || "?").charAt(0).toUpperCase()}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ color: surface.text, fontWeight: 800, fontSize: 14 }}>
                                                {attendant.name}
                                            </Typography>
                                            <Typography sx={{ color: surface.muted, fontSize: 12 }}>
                                                {attendant.openTickets || 0} abertos agora
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </TableCell>
                                <TableCell align="center">
                                    <Chip
                                        size="small"
                                        icon={<StarIcon style={{ fontSize: 16 }} />}
                                        label={formatRating(attendant.rating)}
                                        sx={{
                                            color: isDark ? "#fef3c7" : "#92400e",
                                            backgroundColor: isDark ? "rgba(245,158,11,0.18)" : "#fff7db",
                                            fontWeight: 800,
                                            "& .MuiChip-icon": {
                                                color: isDark ? "#fbbf24" : "#d97706"
                                            }
                                        }}
                                    />
                                </TableCell>
                                <TableCell align="center">{attendant.countRating || 0}</TableCell>
                                <TableCell align="center">{attendant.tickets || 0}</TableCell>
                                <TableCell align="center">{formatTime(attendant.avgWaitTime)}</TableCell>
                                <TableCell align="center">{formatTime(attendant.avgSupportTime)}</TableCell>
                                <TableCell align="center">
                                    {attendant.online ? (
                                        <Tooltip title="Online">
                                            <Chip
                                                size="small"
                                                icon={<CheckCircleIcon style={{ fontSize: 16 }} />}
                                                label="Online"
                                                sx={{
                                                    color: isDark ? "#bbf7d0" : "#166534",
                                                    backgroundColor: isDark ? "rgba(37,211,102,0.16)" : "#dcfce7",
                                                    fontWeight: 800,
                                                    "& .MuiChip-icon": { color: "#25D366" }
                                                }}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Tooltip title="Offline">
                                            <Chip
                                                size="small"
                                                icon={<ErrorIcon style={{ fontSize: 16 }} />}
                                                label="Offline"
                                                sx={{
                                                    color: isDark ? "#e5e7eb" : "#475569",
                                                    backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0",
                                                    fontWeight: 800,
                                                    "& .MuiChip-icon": { color: isDark ? "#cbd5e1" : "#64748b" }
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
